import React, { createContext, useContext, useMemo, ReactNode, useEffect, useState, useCallback, useRef } from "react";

interface ShopifyAppBridgeProviderProps {
  children: ReactNode;
}

// App Bridge v4 CDN exposes this global object
declare global {
  interface Window {
    shopify?: {
      idToken: () => Promise<string>;
      config: {
        apiKey: string;
        host: string;
        shop: string;
      };
      environment: {
        embedded: boolean;
        mobile: boolean;
      };
      toast: {
        show: (message: string, options?: { duration?: number; isError?: boolean }) => void;
      };
      loading: (isLoading: boolean) => void;
      modal: any;
      resourcePicker: any;
      saveBar: any;
    };
  }
}

interface ShopifyContextValue {
  shopify: typeof window.shopify | null;
  isEmbedded: boolean;
  isReady: boolean;
  shop: string | null;
  host: string | null;
  getSessionToken: () => Promise<string | null>;
}

const ShopifyAppBridgeContext = createContext<ShopifyContextValue>({
  shopify: null,
  isEmbedded: false,
  isReady: false,
  shop: null,
  host: null,
  getSessionToken: async () => null,
});

export const useAppBridge = () => {
  return useContext(ShopifyAppBridgeContext);
};

// Safe navigation helper that handles both embedded and non-embedded contexts
export const safeNavigate = (url: string, isEmbedded: boolean): boolean => {
  try {
    if (isEmbedded) {
      // Try window.open with _top target for embedded context
      const result = window.open(url, '_top');
      if (result === null) {
        // Popup was blocked, fall back to location.href
        console.warn('window.open blocked, falling back to location.href');
        window.location.href = url;
      }
    } else {
      window.location.href = url;
    }
    return true;
  } catch (error) {
    console.error('Navigation failed:', error);
    // Final fallback
    window.location.href = url;
    return false;
  }
};

export default function ShopifyAppBridgeProvider({
  children,
}: ShopifyAppBridgeProviderProps) {
  const [urlParams, setUrlParams] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      host: params.get("host"),
      shop: params.get("shop"),
      fromAuth: params.get("from_auth"),
      redirectToConnections: params.get("redirect_to_connections"),
    };
  });

  // Initialize isReady synchronously so a plain mysharestock.org visit never
  // sees a `!isReady` frame. Marketplace visitors have no host/shop param
  // and no ancestorOrigins entry, so we can flip ready immediately; only the
  // embedded flow needs to wait for the App Bridge CDN to load below.
  const [isReady, setIsReady] = useState(() => {
    const potentiallyEmbedded = !!(
      urlParams.host ||
      urlParams.shop ||
      (window.location.ancestorOrigins?.length ?? 0) > 0
    );
    return !potentiallyEmbedded;
  });

  // Track if we're potentially embedded (has Shopify URL params)
  const isPotentiallyEmbedded = useRef(
    !!(urlParams.host || urlParams.shop || window.location.ancestorOrigins?.length > 0)
  );

  // Update URL params on navigation
  useEffect(() => {
    const handleUrlChange = () => {
      const params = new URLSearchParams(window.location.search);
      setUrlParams({
        host: params.get("host"),
        shop: params.get("shop"),
        fromAuth: params.get("from_auth"),
        redirectToConnections: params.get("redirect_to_connections"),
      });
    };

    window.addEventListener('popstate', handleUrlChange);
    return () => window.removeEventListener('popstate', handleUrlChange);
  }, []);

  // Get parameters from URL
  let host = urlParams.host;
  let shop = urlParams.shop;
  const fromAuth = urlParams.fromAuth;
  const redirectToConnections = urlParams.redirectToConnections;

  // Only use sessionStorage for client-side navigation AFTER OAuth is complete
  // This prevents using cached data from a different store during fresh installation
  const isPostAuth = fromAuth || redirectToConnections;

  // Persist host and shop to sessionStorage for navigation (only after auth)
  useEffect(() => {
    if (isPostAuth) {
      if (host) {
        sessionStorage.setItem('shopify_host', host);
      }
      if (shop) {
        sessionStorage.setItem('shopify_shop', shop);
      }
    }
  }, [host, shop, isPostAuth]);

  // Restore from sessionStorage only for client-side navigation (after OAuth is complete)
  // During fresh installation, always use URL params to ensure correct store
  if (isPostAuth) {
    if (!host) {
      const storedHost = sessionStorage.getItem('shopify_host');
      if (storedHost) {
        host = storedHost;
      }
    }
    if (!shop) {
      const storedShop = sessionStorage.getItem('shopify_shop');
      if (storedShop) {
        shop = storedShop;
      }
    }
  }

  // Check if we're in an embedded context (iframe inside Shopify Admin)
  // Shopify only loads the iframe if the app is installed, so we can skip auth redirect
  let isInIframe = false;
  try {
    isInIframe = window.self !== window.top;
  } catch (e) {
    // Cross-origin iframe detection - if we can't access window.top, we're in an iframe
    isInIframe = true;
  }

  const isEmbeddedContext = !!urlParams.host ||
                            isInIframe ||
                            window.shopify?.environment?.embedded;

  // Check if we need to redirect to backend for OAuth verification
  // Skip redirect if:
  // - We're in an embedded context (Shopify already verified app is installed)
  // - We just came from auth (fromAuth=true)
  // - We're being redirected to connections page
  const needsAuthRedirect = urlParams.shop && !fromAuth && !redirectToConnections && !isEmbeddedContext;

  // Check if App Bridge CDN is loaded and ready
  useEffect(() => {
    // Skip if not potentially embedded
    if (!isPotentiallyEmbedded.current) {
      setIsReady(true);
      return;
    }

    const checkAppBridge = () => {
      if (window.shopify) {
        setIsReady(true);
        return true;
      }
      return false;
    };

    // Check immediately
    if (checkAppBridge()) return;

    // Poll for App Bridge to be ready (CDN may take a moment)
    const interval = setInterval(() => {
      if (checkAppBridge()) {
        clearInterval(interval);
      }
    }, 100);

    // Timeout after 5 seconds
    const timeout = setTimeout(() => {
      clearInterval(interval);
      setIsReady(true); // Continue anyway
    }, 5000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, []);

  // Session token getter with built-in error handling and timeout
  const getSessionToken = useCallback(async (): Promise<string | null> => {
    const shopify = window.shopify;
    if (!shopify?.idToken) {
      return null;
    }

    try {
      // Add timeout to prevent hanging forever
      const tokenPromise = shopify.idToken();
      const timeoutPromise = new Promise<null>((_, reject) =>
        setTimeout(() => reject(new Error("Session token timeout")), 5000)
      );

      const token = await Promise.race([tokenPromise, timeoutPromise]);
      return token;
    } catch {
      return null;
    }
  }, []);

  const contextValue = useMemo<ShopifyContextValue>(() => {
    const shopify = window.shopify || null;
    const isEmbedded = shopify?.environment?.embedded ?? false;

    return {
      shopify,
      isEmbedded,
      isReady,
      shop: shop || shopify?.config?.shop || null,
      host: host || shopify?.config?.host || null,
      getSessionToken,
    };
  }, [isReady, shop, host, getSessionToken]);

  // Save the embedded app's shop domain separately (not to override listing's shop)
  useEffect(() => {
    if (shop) {
      const normalizedShop = shop.includes('.myshopify.com')
        ? shop
        : `${shop}.myshopify.com`;
      // Save to a separate key so it doesn't override the listing's shop domain
      localStorage.setItem("embeddedShopDomain", normalizedShop);
    }
  }, [shop]);

  // Redirect to backend for OAuth verification
  useEffect(() => {
    if (needsAuthRedirect) {
      const currentParams = new URLSearchParams(window.location.search);

      // Ensure host is included (from URL, sessionStorage, or App Bridge) to prevent redirect loop
      if (!currentParams.get('host')) {
        const storedHost = sessionStorage.getItem('shopify_host') || window.shopify?.config?.host;
        if (storedHost) {
          currentParams.set('host', storedHost);
        }
      }

      // Mark as embedded if we're in an iframe or App Bridge says so
      if (window.self !== window.top || window.shopify?.environment?.embedded) {
        currentParams.set('embedded', '1');
      }

      window.location.href = `${process.env.REACT_APP_BACKEND_URL}/auth?${currentParams.toString()}`;
    }
  }, [needsAuthRedirect]);

  // Handle redirect to My Connections after OAuth
  useEffect(() => {
    if (redirectToConnections === "true") {
      console.log("OAuth completed. Redirecting to My Connections page...");

      const frontendUrl = process.env.REACT_APP_FRONTEND_URL || 'https://mysharestock.org';
      const targetUrl = `${frontendUrl}/my-biz/my-connections`;

      safeNavigate(targetUrl, contextValue.isEmbedded);
    }
  }, [redirectToConnections, contextValue.isEmbedded]);

  // Show loading while redirecting to backend
  if (needsAuthRedirect) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontFamily: 'sans-serif'
      }}>
        Verifying installation...
      </div>
    );
  }


  return (
    <ShopifyAppBridgeContext.Provider value={contextValue}>
      {children}
    </ShopifyAppBridgeContext.Provider>
  );
}
